import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
} from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import TagModal from "../../components/TagModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import ContactTagListModal from "../../components/ContactTagListModal";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import VisibilityIcon from "@material-ui/icons/Visibility";

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_TAGS":
      return [...state, ...action.payload];
    case "UPDATE_TAGS":
      const tag = action.payload;
      const tagIndex = state.findIndex((s) => s.id === tag.id);
      if (tagIndex !== -1) {
        state[tagIndex] = tag;
        return [...state];
      } else {
        return [tag, ...state];
      }
    case "DELETE_TAGS":
      const tagId = action.payload;
      return state.filter((tag) => tag.id !== tagId);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const styles = {
  container: {
    padding: "24px",
    background: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    marginBottom: "24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
  },
  headerActions: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  },
  button: {
    padding: "10px 20px",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  buttonPrimary: {
    background: "var(--primaryColor, #065183)",
    color: "white",
    padding: "10px",
    minWidth: "auto",
    width: "40px",
    height: "40px",
    justifyContent: "center",
  },
  card: {
    background: "white",
    borderRadius: "8px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    overflow: "hidden",
  },
  tableContainer: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    background: "#f9fafb",
    borderBottom: "2px solid #e8ecf1",
    position: "sticky",
    top: 0,
  },
  tableHeaderCell: {
    padding: "12px 16px",
    textAlign: "center",
    fontSize: "12px",
    fontWeight: 600,
    color: "#666",
    whiteSpace: "nowrap",
  },
  tableRow: {
    borderBottom: "1px solid #e8ecf1",
    transition: "background 0.2s ease",
  },
  tableCell: {
    padding: "12px 16px",
    fontSize: "13px",
    color: "#1a1a1a",
    textAlign: "center",
  },
  chip: {
    display: "inline-block",
    padding: "6px 12px",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: 600,
    color: "white",
    textShadow: "0 1px 1px rgba(0, 0, 0, 0.2)",
  },
  actionButtons: {
    display: "flex",
    gap: "6px",
    justifyContent: "center",
  },
  actionButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    transition: "all 0.2s ease",
    color: "#6c757d",
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#999",
  },
  loadingState: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#666",
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "12px",
    marginTop: "20px",
    padding: "16px",
  },
  paginationButton: {
    padding: "6px 12px",
    border: "1px solid #e8ecf1",
    background: "white",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: 600,
    color: "#1a1a1a",
    transition: "all 0.3s ease",
  },
};

const Tags = () => {
  const { user, socket } = useContext(AuthContext);

  const [selectedTagContacts, setSelectedTagContacts] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [pageSize, setPageSize] = useState(50);
  const [selectedTagName, setSelectedTagName] = useState("");
  const [selectedTag, setSelectedTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [tags, dispatch] = useReducer(reducer, []);
  const [tagModalOpen, setTagModalOpen] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const fetchMoreTags = async () => {
        try {
          const { data } = await api.get("/tags/", {
            params: { searchParam, pageNumber, kanban: 0, limit: pageSize },
          });
          dispatch({ type: "LOAD_TAGS", payload: data.tags });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };

      if (pageNumber > 0) {
        setLoading(true);
        fetchMoreTags();
      }
    }, searchParam ? 200 : 100); // Reduzir delay: 200ms para busca, 100ms para mudanças
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, pageSize]);

  useEffect(() => {
    const onCompanyTags = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_TAGS", payload: data.tag });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_TAGS", payload: +data.tagId });
      }
    };
    socket.on(`company${user.companyId}-tag`, onCompanyTags);
    return () => {
      socket.off(`company${user.companyId}-tag`, onCompanyTags);
    };
  }, [socket, user.companyId]);

  const handleOpenTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(true);
  };

  const handleCloseTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(false);
  };

  const handleSearch = (event) => {
    const newSearchParam = event.target.value.toLowerCase();
    setSearchParam(newSearchParam);
    setPageNumber(1);
    dispatch({ type: "RESET" });
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    setTagModalOpen(true);
  };

  const handleShowContacts = (contacts, tag) => {
    setSelectedTagContacts(contacts);
    setContactModalOpen(true);
    setSelectedTagName(tag);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
    setSelectedTagContacts([]);
    setSelectedTagName("");
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toast.success(i18n.t("tags.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingTag(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <div style={styles.container}>
      {contactModalOpen && (
        <ContactTagListModal
          open={contactModalOpen}
          onClose={handleCloseContactModal}
          tag={selectedTagName}
        />
      )}

      <ConfirmationModal
        title={deletingTag && `${i18n.t("tags.confirmationModal.deleteTitle")}`}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteTag(deletingTag.id)}
      >
        {i18n.t("tags.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      {tagModalOpen && (
        <TagModal
          open={tagModalOpen}
          onClose={handleCloseTagModal}
          aria-labelledby="form-dialog-title"
          tagId={selectedTag && selectedTag.id}
          kanban={0}
        />
      )}

      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>🏷️ {i18n.t("tags.title")}</h1>
        <div style={styles.headerActions}>
          <button
            style={{
              ...styles.button,
              ...styles.buttonPrimary,
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "var(--primaryColor, #065183)";
              e.target.style.opacity = "0.8";
              e.target.style.transform = "translateY(-1px)";
              e.target.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.2)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "var(--primaryColor, #065183)";
              e.target.style.opacity = "1";
              e.target.style.transform = "none";
              e.target.style.boxShadow = "none";
            }}
            onClick={handleOpenTagModal}
            title={i18n.t("tags.newTag")}
          >
            <AddIcon style={{ fontSize: "20px" }} />
          </button>
        </div>
      </div>

      {/* Search */}
      <div style={{ ...styles.card, marginBottom: "24px", padding: "16px" }}>
        <input
          type="text"
          placeholder={i18n.t("tags.searchPlaceholder")}
          value={searchParam}
          onChange={handleSearch}
          style={{
            width: "100%",
            padding: "10px 12px",
            border: "1px solid #e8ecf1",
            borderRadius: "6px",
            fontSize: "13px",
            fontFamily: "inherit",
            background: "#fafbfc",
            transition: "all 0.2s ease",
            boxSizing: "border-box",
          }}
          onFocus={(e) => {
            e.target.style.borderColor = "var(--primaryColor, #065183)";
            e.target.style.background = "white";
            e.target.style.boxShadow = "0 0 0 3px rgba(6, 81, 131, 0.1)";
          }}
          onBlur={(e) => {
            e.target.style.borderColor = "#e8ecf1";
            e.target.style.background = "#fafbfc";
            e.target.style.boxShadow = "none";
          }}
        />
      </div>

      {/* Table */}
      <div style={styles.card} onScroll={handleScroll}>
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead style={styles.tableHeader}>
              <tr>
                <th style={styles.tableHeaderCell}>{i18n.t("tags.table.id")}</th>
                <th style={styles.tableHeaderCell}>{i18n.t("tags.table.tag")}</th>
                <th style={styles.tableHeaderCell}>{i18n.t("tags.table.contacts")}</th>
                <th style={styles.tableHeaderCell}>{i18n.t("tags.table.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {tags.map((tag) => (
                <tr
                  key={tag.id}
                  style={styles.tableRow}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#f9fafb";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <td style={styles.tableCell}>
                    <span style={{ fontWeight: 600, color: "#6c757d" }}>
                      #{tag.id}
                    </span>
                  </td>
                  <td style={styles.tableCell}>
                    <div
                      style={{
                        ...styles.chip,
                        backgroundColor: tag.color,
                      }}
                    >
                      {tag.name}
                    </div>
                  </td>
                  <td style={styles.tableCell}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                      <span style={{ fontWeight: 600, color: "#3b82f6" }}>
                        {tag?.contacts?.length || 0}
                      </span>
                    </div>
                  </td>
                  <td style={styles.tableCell}>
                    <div style={styles.actionButtons}>
                      {tag?.contacts?.length > 0 && (
                        <button
                          style={styles.actionButton}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#f0f0f0";
                            e.currentTarget.style.color = "#1a1a1a";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "none";
                            e.currentTarget.style.color = "#6c757d";
                          }}
                          onClick={() => handleShowContacts(tag?.contacts, tag)}
                          title={i18n.t("tags.viewContacts")}
                        >
                          <VisibilityIcon style={{ fontSize: "16px" }} />
                        </button>
                      )}
                      <button
                        style={styles.actionButton}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f0f0f0";
                          e.currentTarget.style.color = "#1a1a1a";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "none";
                          e.currentTarget.style.color = "#6c757d";
                        }}
                        onClick={() => handleEditTag(tag)}
                        title={i18n.t("tags.edit")}
                      >
                        <EditIcon style={{ fontSize: "16px" }} />
                      </button>
                      <button
                        style={styles.actionButton}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#fee2e2";
                          e.currentTarget.style.color = "#dc2626";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "none";
                          e.currentTarget.style.color = "#6c757d";
                        }}
                        onClick={() => {
                          setConfirmModalOpen(true);
                          setDeletingTag(tag);
                        }}
                        title={i18n.t("tags.delete")}
                      >
                        <DeleteOutlineIcon style={{ fontSize: "16px" }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && tags.length === 0 && (
          <div style={styles.loadingState}>
            <p>{i18n.t("tags.loading")}</p>
          </div>
        )}

        {!loading && tags.length === 0 && (
          <div style={styles.emptyState}>
            <p>{i18n.t("tags.noTagsFound")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tags;
